import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BadgeIndianRupee,
  CalendarClock,
  CheckCircle2,
  CircleSlash,
  Images,
  MapPin,
  MessageCircle,
  Monitor,
  Phone,
  Send,
  Sparkles,
  Tv,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import PublicTopNav from "../components/PublicTopNav";
import StoreBannerCarousel from "./store/StoreBannerCarousel";
import { formatErr, resolveMediaUrl } from "../lib/api";
import { publicApiBase } from "../lib/publicCustomerApi";
import { publicSiteUrl } from "../lib/publicSiteUrl";
import { applySeoTags } from "../lib/seo";
import { usePublicPageBranding } from "../hooks/usePublicPageBranding";

const DEFAULT_SERVICES = [
  { title: "Digital hoarding booking", text: "Plan and book premium outdoor display slots by location, day and campaign duration.", Icon: Monitor },
  { title: "LED wall advertising", text: "High-impact LED wall campaigns for malls, events, streets and high-footfall venues.", Icon: Tv },
  { title: "Indoor screen ads", text: "Place brand messages across retail, clinic, hotel, salon and office screens.", Icon: Images },
  { title: "Slot booking", text: "Reserve time slots for launches, festival offers, political ads or local promotions.", Icon: CalendarClock },
];

const emptyLead = {
  name: "",
  phone: "",
  email: "",
  company: "",
  service: "Digital hoarding booking",
  budget: "",
  preferred_slot: "",
  message: "",
};

function whatsappUrl(number, name) {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "";
  const text = encodeURIComponent(`Hi ${name || "there"}, I want to enquire about advertising slots.`);
  return `https://wa.me/${digits}?text=${text}`;
}

export default function PublicAdAgency({ resolvedHost = null, clientRef: forcedClientRef = "" }) {
  const params = useParams();
  const clientRef = forcedClientRef || params.clientSlug || params.clientRef || "";
  const apiBase = useMemo(() => publicApiBase(resolvedHost), [resolvedHost]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(emptyLead);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!clientRef) {
      setDoc(false);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    axios
      .get(`${apiBase}/providers/${encodeURIComponent(clientRef)}`)
      .then(({ data }) => {
        if (mounted && data?.vertical === "ad_agency") setDoc(data);
        else if (mounted) setDoc(false);
      })
      .catch(() => mounted && setDoc(false))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [apiBase, clientRef]);

  const profile = doc?.profile || {};
  const packages = useMemo(() => (Array.isArray(profile.packages) ? profile.packages : []), [profile.packages]);
  const gallery = useMemo(() => (Array.isArray(profile.gallery) ? profile.gallery : []), [profile.gallery]);
  const banners = Array.isArray(profile.banner_slides) ? profile.banner_slides : [];
  const whatsapp = whatsappUrl(profile.whatsapp_number || doc?.phone, doc?.name);

  usePublicPageBranding(doc?.name || "", profile.image_url || "");

  useEffect(() => {
    if (!doc || doc === false) return;
    const settings = doc.seo_settings || {};
    const title = settings.seo_title || `${doc.name || "Ad Agency"} | Advertising slots and LED wall booking`;
    applySeoTags({
      pageKey: "ad-agency-advertising",
      metaTitle: title,
      metaDescription: settings.meta_description || `Book digital hoardings, LED wall ads, indoor screen slots and campaign packages with ${doc.name || "this ad agency"}.`,
      metaKeywords: settings.meta_keywords || "digital hoarding booking, LED wall advertising, indoor screen ads, ad agency, RP Signage",
      canonicalUrl: settings.canonical_url || "",
      ogTitle: title,
      ogDescription: settings.meta_description || "",
      ogImage: settings.og_image_url || profile.image_url || "",
      h1: title,
      h2: "Digital hoarding booking, LED wall advertising, campaign packages and enquiry leads.",
      robots: "index, follow",
    });
  }, [doc, profile.image_url]);

  const submitLead = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${apiBase}/providers/${encodeURIComponent(clientRef)}/ad-leads`, lead);
      setConfirmed(data);
      setLead(emptyLead);
      toast.success("Enquiry sent. The agency will contact you shortly.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#6B7280] font-mono uppercase tracking-widest">Loading...</div>;
  }

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <CircleSlash className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
          <h2 className="font-display text-2xl font-extrabold">Ad agency not found</h2>
          <p className="text-sm text-[#6B7280] mt-1">This agency link is invalid or inactive.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#F8FAFC_0%,#EFF6FF_46%,#F8FAFC_100%)] text-[#111827]">
      {profile.announcement && (
        <div className="bg-[#111827] text-white text-sm font-medium text-center py-2.5 px-4">
          {profile.announcement}
        </div>
      )}

      <PublicTopNav
        title={doc.name}
        subtitle="Ad Agency"
        siteUrl={publicSiteUrl(clientRef, { resolvedHost, vertical: "ad_agency" })}
        siteName={doc.name}
        brandIcon={profile.image_url ? (
          <img src={resolveMediaUrl(profile.image_url)} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0 bg-white" />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-[#111827] text-white flex items-center justify-center shrink-0">
            <Monitor className="w-5 h-5" />
          </div>
        )}
        trailing={(
          <span className={`text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border ${profile.is_open !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
            {profile.is_open !== false ? "Booking open" : "Currently closed"}
          </span>
        )}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {banners.length > 0 ? (
          <StoreBannerCarousel slides={banners} />
        ) : (
          <section className="overflow-hidden border border-white/70 bg-white shadow-[0_30px_80px_-50px_rgba(15,23,42,0.35)]">
            <div className="relative min-h-[320px] md:min-h-[420px] bg-[#0F172A]">
              {profile.image_url ? (
                <img src={resolveMediaUrl(profile.image_url)} alt={doc.name} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-[linear-gradient(135deg,#0F172A,#1D4ED8_58%,#F59E0B)]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 text-white max-w-4xl">
                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/65 mb-2">Ad Agency storefront</div>
                <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight">
                  {profile.headline || `${doc.name} advertising slots`}
                </h1>
                <p className="mt-4 text-base md:text-lg text-white/85 max-w-2xl">{profile.tagline || "Digital hoarding booking, LED wall advertising and indoor screen campaigns from one local media desk."}</p>
              </div>
            </div>
          </section>
        )}

        <section className="grid gap-4 md:grid-cols-4">
          {DEFAULT_SERVICES.map(({ title, text, Icon }) => (
            <div key={title} className="bg-white border border-[#E5E7EB] p-5 shadow-sm">
              <Icon className="w-6 h-6 text-[#1D4ED8]" />
              <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-[#6B7280] leading-6">{text}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] items-start">
          <div className="bg-[#111827] text-white p-6 md:p-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/50">Campaign packages</div>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Pricing and offers</h2>
            <p className="mt-3 text-sm text-white/70 leading-6">{profile.description || "Choose a media package or send your target location, duration and budget for a custom quote."}</p>
            <div className="mt-6 space-y-3 text-sm text-white/75">
              {profile.hours && <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {profile.hours}</div>}
              {doc.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /> {doc.address}</div>}
              {doc.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {doc.phone}</div>}
            </div>
            {whatsapp && (
              <a href={whatsapp} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-white text-[#111827] px-4 py-3 font-semibold">
                <MessageCircle className="w-4 h-4" /> WhatsApp enquiry
              </a>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {(packages.length ? packages : [
              { id: "starter", title: "Local launch package", price: "Request quote", duration: "7 day campaign", description: "Indoor screen ads plus selected local digital slots.", features: ["Campaign planning", "Creative playback", "Lead follow-up"] },
              { id: "premium", title: "LED impact package", price: "Custom pricing", duration: "15 to 30 days", description: "LED wall and digital hoarding coverage for higher visibility.", features: ["LED wall advertising", "Slot booking", "Performance summary"] },
            ]).map((item) => (
              <div key={item.id || item.title} className="bg-white border border-[#E5E7EB] p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl font-extrabold tracking-tight">{item.title}</h3>
                  <BadgeIndianRupee className="w-5 h-5 text-[#1D4ED8] shrink-0" />
                </div>
                <div className="mt-2 text-sm font-semibold text-[#1D4ED8]">{item.price || "Request quote"}</div>
                {item.duration && <div className="mt-1 text-xs uppercase tracking-wider text-[#6B7280]">{item.duration}</div>}
                <p className="mt-3 text-sm text-[#6B7280] leading-6">{item.description}</p>
                <div className="mt-4 space-y-2">
                  {(item.features || []).slice(0, 4).map((feature) => (
                    <div key={feature} className="flex items-start gap-2 text-sm text-[#374151]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1fr_0.9fr] items-start">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-2">Client ad gallery</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight">Recent advertising placements</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {(gallery.length ? gallery : []).map((item) => (
                <div key={item.id || item.image_url} className="bg-white border border-[#E5E7EB] overflow-hidden shadow-sm">
                  <div className="aspect-[16/10] bg-[#E5E7EB]">
                    <img src={resolveMediaUrl(item.image_url)} alt={item.title || "Ad placement"} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="font-semibold">{item.title || "Campaign placement"}</div>
                    {item.caption && <div className="mt-1 text-sm text-[#6B7280]">{item.caption}</div>}
                  </div>
                </div>
              ))}
              {!gallery.length && (
                <div className="sm:col-span-2 border border-dashed border-[#D1D5DB] bg-white p-8 text-center">
                  <Sparkles className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
                  <h3 className="font-display text-xl font-extrabold">Gallery coming soon</h3>
                  <p className="mt-2 text-sm text-[#6B7280]">Published client ad creatives and placement photos will appear here.</p>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={submitLead} className="bg-white border border-[#E5E7EB] p-5 md:p-6 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-2">Enquiry lead form</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Book a campaign slot</h2>
            {confirmed && <div className="mt-4 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">Enquiry received. Reference {confirmed.id?.slice(0, 8)}.</div>}
            <div className="mt-5 grid gap-4">
              <div><Label>Name</Label><Input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Phone</Label><Input required value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="mt-1 rounded-sm" inputMode="tel" /></div>
              <div><Label>Email</Label><Input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Company</Label><Input value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Service</Label><Input value={lead.service} onChange={(e) => setLead({ ...lead, service: e.target.value })} className="mt-1 rounded-sm" placeholder="LED wall advertising" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Budget</Label><Input value={lead.budget} onChange={(e) => setLead({ ...lead, budget: e.target.value })} className="mt-1 rounded-sm" placeholder="₹ 25,000" /></div>
                <div><Label>Preferred slot</Label><Input value={lead.preferred_slot} onChange={(e) => setLead({ ...lead, preferred_slot: e.target.value })} className="mt-1 rounded-sm" placeholder="Evening / weekend" /></div>
              </div>
              <div><Label>Campaign details</Label><Textarea value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="mt-1 rounded-sm" rows={3} /></div>
              <Button disabled={busy || profile.is_open === false} type="submit" className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">
                <Send className="w-4 h-4 mr-2" /> {busy ? "Sending..." : "Send enquiry"}
              </Button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
